import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useOrganization } from "@/hooks/useOrganization";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Copy, Check, Share2, Loader2, Gift, Users, TrendingUp } from "lucide-react";
import { useTranslation } from "react-i18next";

const _tierBadge: Record<string, string> = { van: "🤝", ambassador: "⭐", legend: "👑" };
const _tierLabel: Record<string, string> = { van: "Vän", ambassador: "Ambassador", legend: "Legend" };

interface Tier {
  name: string;
  min_referrals: number;
  monthly_kickback: number;
  badge: string;
  perks: string[];
}

export default function ReferralSection() {
  // PR-AV: useTranslation() must be called at component scope (not inside
  // a useQuery callback — earlier auto-inject placed it wrong).
  const { t } = useTranslation();
  const { user } = useAuth();
  const { organization } = useOrganization();
  const [copied, setCopied] = useState(false);

  // Get referral config
  const { data: config } = useQuery({
    queryKey: ["referral-config", organization?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("referral_config")
        .select("*")
        .eq("organization_id", organization!.id)
        .maybeSingle();
      return data;
    },
    enabled: !!organization?.id,
  });

  // Get user's referral code
  const { data: codeData, isLoading: codeLoading } = useQuery({
    queryKey: ["my-referral-code", user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("referral_codes")
        .select("code")
        .eq("user_id", user!.id)
        .maybeSingle();
      return data;
    },
    enabled: !!user?.id,
  });

  // Get referrals
  const { data: referrals = [] } = useQuery({
    queryKey: ["my-referrals", user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("referrals")
        .select("id, status, created_at, activated_at, referred_user_id, profiles!referrals_referred_user_id_fkey(full_name)")
        .eq("referrer_user_id", user!.id)
        .order("created_at", { ascending: false });
      return data ?? [];
    },
    enabled: !!user?.id,
  });

  // Get total earned
  const { data: totalEarned } = useQuery({
    queryKey: ["referral-total-earned", user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("referral_rewards")
        .select("amount")
        .eq("referrer_user_id", user!.id);
      return (data ?? []).reduce((sum, r) => sum + Number(r.amount), 0);
    },
    enabled: !!user?.id,
  });

  // Generate code mutation
  const generateCode = useMutation({
    mutationFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-referral-code`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${session?.access_token}`,
            "Content-Type": "application/json",
          },
        }
      );
      const json = await res.json();
      if (json.error) throw new Error(json.error);
      return json;
    },
    onSuccess: () => {
      toast.success(t("payments:toast.referralCodeCreated"));
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const tiers = (config?.tiers || []) as Tier[];
  const activeCount = referrals.filter((r: Record<string, unknown>) => r.status === "active").length;

  // Determine current tier
  let currentTier: Tier | null = null;
  let nextTier: Tier | null = null;
  const sortedTiers = [...tiers].sort((a, b) => a.min_referrals - b.min_referrals);
  for (let i = 0; i < sortedTiers.length; i++) {
    if (activeCount >= sortedTiers[i].min_referrals) {
      currentTier = sortedTiers[i];
      nextTier = sortedTiers[i + 1] || null;
    }
  }

  const code = codeData?.code;
  const shareUrl = code ? `${window.location.origin}/auth?ref=${code}` : "";

  const copyCode = () => {
    navigator.clipboard.writeText(shareUrl || code || "");
    setCopied(true);
    toast.success(t("payments:toast.linkCopied"));
    setTimeout(() => setCopied(false), 2000);
  };

  const shareLink = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Kom och spela padel!",
          text: `Gå med i ${organization?.name || "klubben"} och få välkomstbonus!`,
          url: shareUrl,
        });
      } catch {
        copyCode();
      }
    } else {
      copyCode();
    }
  };

  if (!config?.enabled) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Gift className="h-5 w-5 text-primary" />
          Värvningsprogram
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Current tier */}
        {currentTier && (
          <div className="flex items-center gap-3 p-3 rounded-lg bg-primary/5">
            <span className="text-2xl">{currentTier.badge}</span>
            <div>
              <p className="font-semibold">{currentTier.name}</p>
              <p className="text-sm text-muted-foreground">
                {activeCount} aktiva värvningar &middot; {currentTier.monthly_kickback * activeCount} kr/mån
              </p>
            </div>
          </div>
        )}

        {/* Tier progress */}
        <div className="flex gap-2">
          {sortedTiers.map((tier) => {
            const isActive = activeCount >= tier.min_referrals;
            const isCurrent = currentTier?.name === tier.name;
            return (
              <div
                key={tier.name}
                className={`flex-1 text-center p-2 rounded-lg border ${
                  isCurrent ? "border-primary bg-primary/10" : isActive ? "border-green-300 bg-green-50" : "border-muted"
                }`}
              >
                <span className="text-lg">{tier.badge}</span>
                <p className="text-xs font-medium mt-1">{tier.name}</p>
                <p className="text-xs text-muted-foreground">{tier.monthly_kickback} kr/mån</p>
                {isActive && <span className="text-xs text-green-600">✓</span>}
              </div>
            );
          })}
        </div>

        {nextTier && (
          <p className="text-sm text-muted-foreground text-center">
            {nextTier.min_referrals - activeCount} värvningar till <strong>{nextTier.name}</strong>
          </p>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 gap-3">
          <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/50">
            <Users className="h-4 w-4 text-muted-foreground" />
            <div>
              <p className="text-sm font-medium">{activeCount}/{referrals.length}</p>
              <p className="text-xs text-muted-foreground">Aktiva/Totalt</p>
            </div>
          </div>
          <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/50">
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
            <div>
              <p className="text-sm font-medium">{totalEarned ?? 0} kr</p>
              <p className="text-xs text-muted-foreground">Total intjänad</p>
            </div>
          </div>
        </div>

        {/* Referral list */}
        {referrals.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-semibold">Dina värvningar</h4>
            {referrals.map((ref: Record<string, unknown>) => (
              <div key={ref.id} className="flex items-center justify-between text-sm p-2 rounded bg-muted/30">
                <span>
                  {ref.status === "active" ? "✅" : ref.status === "paused" ? "⏸️" : "⏳"}{" "}
                  {ref.profiles?.full_name || "Okänd"}
                </span>
                <Badge variant={ref.status === "active" ? "default" : "secondary"} className="text-xs">
                  {ref.status === "active" ? "Aktiv" : ref.status === "paused" ? "Pausad" : "Väntar"}
                </Badge>
              </div>
            ))}
          </div>
        )}

        {/* Share code */}
        {code ? (
          <div className="space-y-2">
            <h4 className="text-sm font-semibold">Dela din kod</h4>
            <div className="flex gap-2">
              <Input value={code} readOnly className="font-mono" />
              <Button variant="outline" size="icon" onClick={copyCode}>
                {copied ? <Check className="h-4 w-4 text-primary" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1 gap-1" onClick={shareLink}>
                <Share2 className="h-4 w-4" /> Dela länk
              </Button>
            </div>
          </div>
        ) : (
          <Button
            className="w-full gap-2"
            onClick={() => generateCode.mutate()}
            disabled={generateCode.isPending || codeLoading}
          >
            {generateCode.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Gift className="h-4 w-4" />}
            Skapa din värvningskod
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
